<template>
  <div class="events-page">
    <div class="container py-5">
      <!-- Page Header -->
      <div class="text-center mb-5">
        <h1 class="display-5 fw-bold text-primary mb-3">Offline Events</h1>
        <p class="lead text-muted">Join our community events and connect with others</p>
      </div>

      <!-- Search Bar -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="input-group input-group-lg">
            <span class="input-group-text bg-white">
              <i class="bi bi-search"></i>
            </span>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Search events by name, location, or description..."
              aria-label="Search events"
              @input="handleSearch"
            />
            <button
              v-if="searchQuery"
              class="btn btn-outline-secondary"
              type="button"
              @click="clearSearch"
              aria-label="Clear search"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Category Filter -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="d-flex flex-wrap justify-content-center gap-2">
            <button
              v-for="category in categories"
              :key="category.value"
              class="btn category-btn"
              :class="{ 'active': selectedCategory === category.value }"
              @click="selectedCategory = category.value"
              :aria-pressed="selectedCategory === category.value"
              role="button"
            >
              <i :class="category.icon" class="me-2" aria-hidden="true"></i>
              {{ category.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- View Toggle -->
      <div class="row mb-4">
        <div class="col-12 d-flex justify-content-between align-items-center">
          <div class="btn-group" role="group">
            <button
              type="button"
              class="btn btn-outline-primary"
              :class="{ 'active': viewMode === 'list' }"
              @click="viewMode = 'list'"
            >
              <i class="bi bi-list-ul"></i> List View
            </button>
            <button
              type="button"
              class="btn btn-outline-primary"
              :class="{ 'active': viewMode === 'map' }"
              @click="viewMode = 'map'"
            >
              <i class="bi bi-map"></i> Map View
            </button>
            <button
              type="button"
              class="btn btn-outline-primary"
              :class="{ 'active': viewMode === 'calendar' }"
              @click="viewMode = 'calendar'"
            >
              <i class="bi bi-calendar3"></i> Calendar View
            </button>
          </div>

          <div class="text-muted">
            <i class="bi bi-calendar-event"></i>
            {{ filteredEvents.length }} events found
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- List View -->
      <div v-else-if="viewMode === 'list'" class="row">
        <div v-if="filteredEvents.length === 0" class="col-12 text-center py-5">
          <i class="bi bi-calendar-x display-1 text-muted"></i>
          <p class="text-muted mt-3">No events found in this category</p>
        </div>

        <div
          v-for="event in filteredEvents"
          :key="event.id"
          class="col-md-6 col-lg-4 mb-4"
        >
          <div class="card event-card h-100 shadow-sm" @click="goToEventDetail(event.id)">
            <div class="event-image-wrapper">
              <img
                :src="event.image_url || require('@/assets/logo.svg')"
                class="card-img-top event-image"
                :alt="event.title"
              />
              <span class="badge category-badge">{{ getCategoryLabel(event.category) }}</span>
              <span v-if="isEventFull(event)" class="badge full-badge">FULL</span>
            </div>
            <div class="card-body">
              <h5 class="card-title">{{ event.title }}</h5>
              <div class="event-details mb-3">
                <div class="detail-item">
                  <i class="bi bi-calendar3 text-primary"></i>
                  <span>{{ formatDate(event.time_start) }}</span>
                </div>
                <div class="detail-item">
                  <i class="bi bi-geo-alt text-primary"></i>
                  <span>{{ event.location }}</span>
                </div>
                <div class="detail-item">
                  <i class="bi bi-people text-primary"></i>
                  <span>{{ event.participants }} / {{ event.capacity }} registered</span>
                </div>
              </div>
              <div class="progress mb-3" style="height: 8px;">
                <div
                  class="progress-bar"
                  :class="getProgressBarClass(event)"
                  :style="{ width: getOccupancyRate(event) + '%' }"
                ></div>
              </div>
              <button class="btn btn-primary w-100">
                <i class="bi bi-info-circle me-2"></i>View Details
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Map View -->
      <div v-else-if="viewMode === 'map'" class="row">
        <div class="col-12">
          <EventMap
            :events="filteredEvents"
            @nearby-events="handleNearbyEvents"
          />

          <!-- Nearby Events List -->
          <div v-if="nearbyEvents.length > 0" class="nearby-events mt-4">
            <h5>Nearby Events (within 10km)</h5>
            <div class="list-group">
              <a
                v-for="item in nearbyEvents"
                :key="item.event.id"
                href="#"
                class="list-group-item list-group-item-action"
                @click.prevent="goToEventDetail(item.event.id)"
              >
                <div class="d-flex w-100 justify-content-between">
                  <h6 class="mb-1">{{ item.event.title }}</h6>
                  <small class="text-muted">{{ item.distance }}km away</small>
                </div>
                <p class="mb-1 text-muted small">{{ item.event.location }}</p>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Calendar View -->
      <div v-else-if="viewMode === 'calendar'" class="row">
        <div class="col-12">
          <CalendarViewContent
            :events="filteredEvents"
            :selected-category="selectedCategory"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllEvents } from '@/services/eventService'
import EventMap from '@/views/event/EventMap.vue'
import CalendarViewContent from './event/calendar/CalendarViewContent.vue'

export default {
  name: 'EventsPage',
  components: {
    EventMap,
    CalendarViewContent
  },
  data() {
    return {
      events: [],
      selectedCategory: 'all',
      viewMode: 'list',
      loading: true,
      nearbyEvents: [],
      searchQuery: '',
      categories: [
        { value: 'all', label: 'All Events', icon: 'bi bi-grid' },
        { value: 'Fitness Class', label: 'Fitness Class', icon: 'bi bi-activity' },
        { value: 'Health Talk', label: 'Health Talk', icon: 'bi bi-chat-text' },
        { value: 'Outdoor Hiking', label: 'Outdoor Hiking', icon: 'bi bi-tree' },
        { value: 'Wellness Salon', label: 'Wellness Salon', icon: 'bi bi-hearts' },
        { value: 'Other', label: 'Other', icon: 'bi bi-three-dots' }
      ]
    }
  },
  computed: {
    filteredEvents() {
      let filtered = this.events

      // Filter by category
      if (this.selectedCategory !== 'all') {
        filtered = filtered.filter(event => event.category === this.selectedCategory)
      }

      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim()
        filtered = filtered.filter(event =>
          event.title.toLowerCase().includes(query) ||
          event.location.toLowerCase().includes(query) ||
          event.description.toLowerCase().includes(query)
        )
      }

      return filtered
    }
  },
  async mounted() {
    await this.loadEvents()
  },
  methods: {
    async loadEvents() {
      try {
        this.loading = true
        this.events = await getAllEvents({ status: 'active' })
      } catch (error) {
        console.error('Error loading events:', error)
        alert('Failed to load events. Please try again.')
      } finally {
        this.loading = false
      }
    },
    goToEventDetail(eventId) {
      this.$router.push(`/events/${eventId}`)
    },
    handleNearbyEvents(nearbyEvents) {
      this.nearbyEvents = nearbyEvents
    },
    handleSearch() {
      // Search is handled through computed property
      // This method can be used for analytics or debouncing if needed
    },
    clearSearch() {
      this.searchQuery = ''
    },
    getCategoryLabel(categoryValue) {
      const category = this.categories.find(c => c.value === categoryValue)
      return category ? category.label : categoryValue
    },
    formatDate(timestamp) {
      if (!timestamp) return 'TBA'

      try {
        let date
        if (timestamp.toDate) {
          date = timestamp.toDate()
        } else if (timestamp.seconds) {
          date = new Date(timestamp.seconds * 1000)
        } else {
          date = new Date(timestamp)
        }

        const options = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }
        return date.toLocaleDateString('en-US', options)
      } catch (error) {
        return 'TBA'
      }
    },
    isEventFull(event) {
      return event.participants >= event.capacity
    },
    getOccupancyRate(event) {
      return Math.min((event.participants / event.capacity) * 100, 100)
    },
    getProgressBarClass(event) {
      const rate = this.getOccupancyRate(event)
      if (rate >= 100) return 'bg-danger'
      if (rate >= 75) return 'bg-warning'
      return 'bg-success'
    }
  }
}
</script>

<style scoped>
.events-page {
  min-height: calc(100vh - 100px);
  background: linear-gradient(135deg, #fef7f7 0%, #f3e5f5 100%);
}

.category-btn {
  border: 2px solid #e0e0e0;
  background: white;
  color: #666;
  border-radius: 25px;
  padding: 8px 20px;
  transition: all 0.3s ease;
}

.category-btn:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  transform: translateY(-2px);
}

.category-btn.active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
}

.event-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  border-radius: 12px;
  overflow: hidden;
}

.event-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.event-image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.event-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.event-card:hover .event-image {
  transform: scale(1.1);
}

.category-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(255, 255, 255, 0.95);
  color: var(--primary-color);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.full-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(220, 53, 69, 0.95);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.event-details {
  font-size: 0.9rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #666;
}

.detail-item i {
  font-size: 1rem;
}

.map-placeholder {
  min-height: 500px;
  border-radius: 12px;
  border: 2px dashed #ddd;
}

.btn-primary {
  background: linear-gradient(135deg, #9C27B0 0%, #E91E63 100%);
  border: none;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(156, 39, 176, 0.4);
}

@media (max-width: 768px) {
  .event-image-wrapper {
    height: 180px;
  }

  .category-btn {
    font-size: 0.85rem;
    padding: 6px 14px;
  }
}
</style>
